import { computed, defineComponent, nextTick, PropType, reactive, ref } from '@vue/composition-api';
import AdminCompanyCredits from '@/components/admin-company-credits/admin-company-credits.vue';
import { setDefaultPromotionType } from '@/functions/services-functions';
import { allPromotions, allPromotionsWithNone, PromotionType } from '@/states/promotion-state';
import { importedJobsClient } from './client';
import { issueSection } from './correction-editor';
import { errorStatus, recruitJobUrl, validationMessages } from './helpers';
import type { ImportedJobDetail } from './types';

type Issue = ImportedJobDetail['issues'][number];

export default defineComponent({
  components: { AdminCompanyCredits },
  props: {
    detail: { type: Object as PropType<ImportedJobDetail>, required: true },
    disabled: { type: Boolean, default: false },
    dirty: { type: Boolean, default: false },
  },
  setup(props, context) {
    const dialog = ref<HTMLDialogElement | null>(null);
    const state = reactive({
      panel: '',
      promotion: '' as PromotionType | '',
      saving: false,
      saved: false,
      error: false,
      messages: [] as string[],
    });
    const promotions = computed(() => allPromotionsWithNone.filter(
      type => props.detail.format !== 'hailey-json' || type !== 'none'
    ));
    const companyLink = computed(() => ({
      name: 'Admin Companies', query: { search: props.detail.companyName },
    }));
    const jobLink = computed(() => props.detail.jobAdId
      ? recruitJobUrl(props.detail.companyId, props.detail.jobAdId)
      : null);
    const recovery = (issue: Issue) => {
      if (issueSection(issue.field)) return 'field';

      switch (issue.code) {
        case 'Promotion': return props.detail.supportsDefaultPromotion ? 'promotion' : 'company';
        case 'Credits': return 'credits';
        case 'Rules': return 'rules';
        case 'Connection':
        case 'SalesPerson': return 'company';
        case 'PromotionExpired':
        case 'JobState': return 'job';
        case 'Configuration': return 'configuration';
        case 'SourceRefresh': return 'refresh-source';
        case 'MatrixTags': return 'matrix-approval';
        case 'MatrixUnavailable': return 'matrix-retry';
        default: return 'source';
      }
    };
    const canChoose = (issue: Issue) => props.detail.canChoosePromotion &&
      ['Promotion', 'Credits', 'PromotionExpired'].includes(issue.code);
    const paidPromotions = computed(() => allPromotions.filter(
      type => props.detail.promotionCredits.some(credit => credit.promotion === type)
    ));
    const creditAmount = (promotion: string) => props.detail.promotionCredits
      .find(credit => credit.promotion === promotion)?.amount ?? 0;
    const paymentKey = (promotion: string) => creditAmount(promotion) > 0 ? 'uses-credits' : 'will-be-purchased';
    const willPurchase = computed(() => !!state.promotion && creditAmount(state.promotion) <= 0);
    const canConfirmChoice = computed(() => !!state.promotion && paidPromotions.value.includes(state.promotion as never) &&
      !state.saving && !props.disabled && !props.dirty);
    const open = async (panel: string) => {
      if (props.disabled || props.dirty) return;

      state.panel = panel;
      const preset = props.detail.defaultPromotionType || '';
      state.promotion = panel !== 'choose-promotion' || paidPromotions.value.includes(preset as never) ? preset : '';
      state.saved = false;
      state.error = false;
      state.messages = [];
      await nextTick();
      dialog.value?.showModal();
    };
    const close = () => {
      if (state.saving) return;

      dialog.value?.close();
      state.panel = '';
    };
    const savePromotion = async () => {
      if (props.disabled || props.dirty || state.saving || !state.promotion ||
          !props.detail.supportsDefaultPromotion || !promotions.value.includes(state.promotion)) {
        return;
      }

      state.saving = true;
      state.error = false;
      state.saved = false;
      try {
        const saved = await setDefaultPromotionType(props.detail.serviceId, state.promotion);
        if (!saved) {
          state.error = true;
          return;
        }

        state.saved = true;
        context.emit('saved');
      } catch {
        state.error = true;
      } finally {
        state.saving = false;
      }
    };

    const chooseStoredPromotion = async () => {
      if (props.disabled || props.dirty || state.saving || !state.promotion ||
          !props.detail.canChoosePromotion || !paidPromotions.value.includes(state.promotion as never)) {
        return;
      }

      state.saving = true;
      state.messages = [];
      try {
        const updated = await importedJobsClient.choosePromotion(
          props.detail.id, props.detail.version, state.promotion as PromotionType
        );
        dialog.value?.close();
        state.panel = '';
        context.emit('updated', updated);
      } catch (error) {
        state.messages = validationMessages(error);
        if (errorStatus(error) === 409) context.emit('stale');
        state.error = !state.messages.length;
      } finally {
        state.saving = false;
      }
    };

    return {
      dialog, state, promotions, paidPromotions, willPurchase, canConfirmChoice, companyLink, jobLink, recovery, canChoose, creditAmount,
      paymentKey, issueSection, open, close, savePromotion, chooseStoredPromotion,
    };
  },
});
